import { ScreenDesignDocumentDto } from "@kuika/kuika-cl-model";
import { Form } from "antd/es";
import { AxiosResponse } from "axios";
import * as React from "react";
import { ScreenDesignDocumentService } from "../../../../services/document-service";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../shared/utility/save-status";
import { KModal } from "../../../ui-kit/k-modal/k-modal";
import { KModalFooter } from "../../../ui-kit/k-modal/k-modal-footer";
import { type KuikaButtonStatus } from "../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaModalIconWrapper from "../../../ui-kit/kuika-modal-icon-wrapper/kuika-modal-icon-wrapper";
import KuikaH5 from "../../../ui-kit/typography/H5/H5";
import KuikaSmallText from "../../../ui-kit/typography/SmallText/SmallText";
import "./document-modal.scss";

/** The `id` the footer's submit button targets — see `KModalFooter.submitForm`. */
const FORM_ID: string = "screen-document-form";

export interface IDocumentModalState {
  /** Where Create / Update stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
}

export interface IDocumentModalProps {
  closeModal: (reason?: string) => void;
  showModal: boolean;
  screenId: string;
  reloadDocumentList: () => void;
  documentId: string;
  documentName: string;
  documentLink: string;
}

export interface IDocumentFormValues {
  name: string;
  documentUrl: string;
}

class DocumentModal extends React.PureComponent<IDocumentModalProps & IWithSDProps, IDocumentModalState> {
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  constructor(props: IDocumentModalProps & IWithSDProps) {
    super(props);
    this.state = {
      saveStatus: "idle"
    };
  }

  componentWillUnmount(): void {
    this.saveStatus.dispose();
  }

  onSubmit = async (data: IDocumentFormValues): Promise<void> => {
    // The commit is live again as soon as the ✓ replaces the spinner, so a second Enter would
    // re-send the save behind the closing dialog.
    if (!this.saveStatus.canStart) return;
    const isDocumentAlreadyExist = this.props.sd.projectDocumentList.find(
      (x) => x.name === data.name && x.id !== this.props.documentId
    );
    if (isDocumentAlreadyExist) {
      KMainFunctions.displayWarningNotification("Document name already exist!");
      return;
    }
    try {
      this.saveStatus.start();
      const obj = {} as ScreenDesignDocumentDto;
      obj.KScreenDesign_Id = this.props.screenId;
      obj.name = data.name;
      if (data.documentUrl.includes("https://") || data.documentUrl.includes("http://")) {
        obj.link = data.documentUrl;
      } else {
        obj.link = `http://${data.documentUrl}`;
      }
      obj.version = "abc";

      const isEditing: boolean = Boolean(this.props?.documentId);
      if (isEditing) {
        obj.id = this.props.documentId;
      }
      const request: Promise<AxiosResponse> = isEditing
        ? ScreenDesignDocumentService.Update(obj)
        : ScreenDesignDocumentService.Create(obj);
      const res: AxiosResponse = await request;
      this.props.reloadDocumentList();
      if (res?.status !== 200) {
        // A resolved response that is not 200: the dialog stays, and its button says so.
        this.saveStatus.fail();
        return;
      }
      // The ✓ goes up first and the dialog leaves after it — see `SaveStatus`. The saved
      // document is then a row in the list behind, so there is nothing for a toast to add.
      this.saveStatus.succeed((): void => this.props.closeModal("success"));
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
      // A failed save closes the dialog, as it did; its button is not left on "Saving".
      this.saveStatus.reset();
      this.props.closeModal();
    }
  };

  render() {
    const isEditing: boolean = Boolean(this.props?.documentId);

    return (
      <KModal
        showModal={this.props.showModal}
        closeModal={() => this.props.closeModal()}
        header={isEditing ? "Edit Document" : "New Document"}
        footer={
          <KModalFooter
            onCancel={() => this.props.closeModal()}
            cancelTestId="cancelButton"
            submitLabel={isEditing ? "Update" : "Create"}
            submitTestId="documentSubmitButton"
            submitForm={FORM_ID}
            status={this.state.saveStatus}
          />
        }
        width={460}
        centered
        isLoading={this.state.saveStatus === "loading"}
      >
        <Form
          id={FORM_ID}
          name="screenDocument"
          onFinish={this.onSubmit}
          initialValues={
            isEditing
              ? {
                  name: this.props?.documentName,
                  documentUrl: this.props?.documentLink
                }
              : undefined
          }
        >
          <div className="kuika-document-modal">
            <div className="kuika-document-modal__header">
              <KuikaModalIconWrapper iconName="file-text" intent={isEditing ? "edit" : "create"} />
              <div className="kuika-document-modal__title">
                <KuikaH5>{isEditing ? "Edit Document" : "New Document"}</KuikaH5>
              </div>
            </div>
            <div className="kuika-document-modal__form-item">
              <KuikaSmallText className="kuika-document-modal__label">Name</KuikaSmallText>
              <Form.Item name="name" rules={[{ required: true, message: "Name should not empty!" }]}>
                <KuikaInput data-testid="documentNameInput" placeholder="Please enter a name" size="md" autoFocus />
              </Form.Item>
            </div>

            <div className="kuika-document-modal__form-item">
              <KuikaSmallText className="kuika-document-modal__label">URL</KuikaSmallText>
              <Form.Item
                name="documentUrl"
                rules={[
                  { required: true, message: "Please enter a URL" },
                  { message: "Please enter a valid URL", type: "url" }
                ]}
              >
                <KuikaInput data-testid="documentUrlInput" placeholder="Please enter a url" size="md" />
              </Form.Item>
            </div>
          </div>
        </Form>
      </KModal>
    );
  }
}

const documentModal = withSD<IDocumentModalProps>(DocumentModal);
export { documentModal as DocumentModal };
